import { useEffect, useCallback, useState } from 'react'

import { nanoid } from 'nanoid'

import { useBlock } from '@tribeplatform/react-slate-kit/hooks'

import { useI18n } from '../../i18n/index.js'
import type {
  MultiViewBlockProps,
  View,
  StaticField,
  SetProperties,
} from '../types.js'
import { convertToViewField } from '../utils.js'

interface Props<T> {
  genericView: T
  staticFields: StaticField[]
}
export function useMultiViewBlock<T extends View>({
  genericView,
  staticFields,
}: Props<T>) {
  const { $t } = useI18n()
  const [selectedViewId, setSelectedViewId] = useState<string>(null)
  const { block, upsertProp, upsertProps, updateExtraContext } =
    useBlock<MultiViewBlockProps<T>>()
  const { views, title, description, linkText, linkUrl } = {
    ...block.config.defaultProps,
    ...block.props,
  }
  const { views: compiledViews } = block.compiledProps

  const setActiveViewId = useCallback(
    (id: number) => {
      updateExtraContext({ activeViewIndex: id })
    },
    [updateExtraContext],
  )

  const showViewSettings = (id: string, index: number) => {
    setSelectedViewId(id)
    setActiveViewId(index)
  }

  const hideViewSettings = () => {
    setSelectedViewId(null)
    setActiveViewId(0)
  }

  const addView = useCallback(
    (name: string) => {
      const id = nanoid()
      const newView: T = {
        ...genericView,
        fields: convertToViewField(staticFields),
        id,
        name,
      }

      upsertProp('views', [...views, newView])
    },
    [genericView, staticFields, upsertProp, views],
  )

  const setViews = useCallback(
    (newViews: T[]) => {
      upsertProp('views', newViews)
    },
    [upsertProp],
  )

  const setProperties = useCallback<SetProperties<T>>(
    props => {
      const newViews = views.map(view => {
        if (view.id === selectedViewId) {
          const newView: T = {
            ...view,
            ...props,
          }

          return newView
        }

        return view
      })

      upsertProp('views', newViews)
    },
    [selectedViewId, upsertProp, views],
  )

  const setTitle = useCallback(
    (title: string) => {
      upsertProp('title', title)
    },
    [upsertProp],
  )

  const setDescription = useCallback(
    (description: string) => {
      upsertProp('description', description)
    },
    [upsertProp],
  )

  const setLinkText = useCallback(
    (linkText: string) => {
      upsertProp('linkText', linkText)
    },
    [upsertProp],
  )

  const setLinkUrl = useCallback(
    (linkUrl: string) => {
      upsertProp('linkUrl', linkUrl)
    },
    [upsertProp],
  )

  const setHeadingLink = useCallback(
    (linkText: string, linkUrl: string) => {
      upsertProps({ linkText, linkUrl })
    },
    [upsertProps],
  )

  // creating the default view
  useEffect(() => {
    if (!views?.length) {
      addView($t({ defaultMessage: 'Default', id: 'Generics.Default' }))
    }
  }, [$t, addView, views?.length])

  return {
    selectedViewId,
    showViewSettings,
    hideViewSettings,
    setViews,
    addView,
    setProperties,
    setTitle,
    setDescription,
    setLinkText,
    setLinkUrl,
    setHeadingLink,
    title,
    description,
    linkText,
    linkUrl,
    views,
    compiledViews,
  }
}
